import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import queryString from 'query-string';
import { Box } from 'grommet';
import {
  refreshReportAccessDataRequest,
  clearReportAccessDataRequest,
} from '@Actions';
import Mixpanel from '@Helpers/mixpanel';
import {
  secondsSinceEpoch, baseServerUrl, selectEnvironment,
} from '@Helpers/util';
import { useAppTheme } from '@Components/Context';
import { DomoEmbedPartial } from '@Components/Partial/Product/Overview';


const NoAuthReportPage = ({
  small,
  loading,
  location,
  refreshAccessData,
  clearAccessData,
  accessToken = null,
  accessTokenExpiration = null,
}) => {
  const [embedExpired, setEmbedExpired] = React.useState(false);
  const [embedLinkInvalid, setEmbedLinkInvalid] = React.useState(false);
  const [embedToken, setEmbedToken] = React.useState(null);
  const [embedData, setEmbedData] = React.useState({
    embedId: null,
    embedTitle: null,
    applyCompanyFilter: null,
    companyId: null,
    pageTitle: null,
  });
  const { theme: { page } } = useAppTheme();
  const useSearch = location.search.substring(1);
  const decodedParams = atob(useSearch);

  React.useEffect(() => {
    if (decodedParams) {
      const params = queryString.parse(decodedParams);
      const expiresAt = parseInt(params.expiresAt, 10);

      if (expiresAt < secondsSinceEpoch()) {
        setEmbedExpired(true);
      } else {
        setEmbedData({
          embedId: params.embedId,
          embedTitle: params.embedTitle,
          applyCompanyFilter: params.applyCompanyFilter && params.applyCompanyFilter === 'true',
          companyId: parseInt(params.companyId, 10),
          pageTitle: 'Shared Report',
        });

        Mixpanel.track('shared_report_view', {
          path: location.pathname,
          embedTitle: params.embedTitle,
          embedId: params.embedId,
          environment: selectEnvironment(),
        });
      }
    } else {
      setEmbedLinkInvalid(true);
    }
  }, [decodedParams]);

  const fetchServerEmbedToken = async (useToken) => {
    const options = { method: 'GET' };
    let embedParams = `token=${useToken}&embedId=${embedData.embedId}`;

    if (embedData.applyCompanyFilter) {
      embedParams += `&companyId=${embedData.companyId}`;
    }

    const response = await fetch(`${baseServerUrl()}api/embedToken?${embedParams}`, options);
    const responseContent = await response.text();
    setEmbedToken(responseContent);
  };

  React.useEffect(() => {
    if (!accessToken || accessTokenExpiration - 60 > secondsSinceEpoch()) {
      refreshAccessData();
    }

    return () => {
      clearAccessData();
    };
  }, []);

  React.useEffect(() => {
    if (accessToken && embedData.embedId && !embedLinkInvalid && !embedExpired) {
      fetchServerEmbedToken(accessToken);
    }
  }, [accessToken, embedData, embedLinkInvalid, embedExpired]);

  return (
    <Box flex background={page.altBg} pad={{ top: '7.2rem', bottom: '1rem', horizontal: small ? '0.5rem' : '4vw' }} height={{ min: '100vh' }}>
      <DomoEmbedPartial
        noAuth
        loading={loading}
        textColor={page.primaryText}
        highlightColor={page.highlight}
        embedId={embedData.embedId}
        embedToken={embedToken}
        embedExpired={embedExpired}
        embedLinkInvalid={embedLinkInvalid}
      />
    </Box>
  );
};

function mapStateToProps(state) {
  return {
    accessToken: state.productEmbed.reportAccessToken,
    accessTokenExpiration: state.productEmbed.reportAccessTokenExpiration,
    loading: state.fetchLoader.dataLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    refreshAccessData: refreshReportAccessDataRequest,
    clearAccessData: clearReportAccessDataRequest,
  }, dispatch);
}

NoAuthReportPage.propTypes = {
  small: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  refreshAccessData: PropTypes.func.isRequired,
  clearAccessData: PropTypes.func.isRequired,
  accessToken: PropTypes.string,
  accessTokenExpiration: PropTypes.number,
};

export default connect(mapStateToProps, mapDispatchToProps)(NoAuthReportPage);
